import { toast } from '@/hooks/use-toast'
import { useGoNextMedRequest } from '@/hooks/useGoNextMedRequest'
import { usePosthogCustom } from '@/hooks/usePosthogCustom'
import useSendFanConsultation from '@/hooks/useSendFanConsultation'
import { canSendMessages } from '@/lib/permissions'
import {
  resetMedicationRequestStore,
  selectIsDecisionSubmitting,
  useMedicationRequestStore
} from '@/lib/stores/medication-request'
import { isMpsMedRequest, isMultiProductOrderMedRequest, medReqFieldValidationNotifications } from '@/lib/utils'
import type { MedRequest } from '@/types/data'
import type { ActionPlan } from '@/lib/med-requests'
import useMedRequestAction, { invalidateRelevantActionQueries, type MedRequestMutationFnProps } from './action'
import { mpsFamilyDecisionActions, runMedRequestActionChain } from './mps-family-actions'
import { multiProductOrderDecisionPlan } from './multi-product-order/family-actions'

export type OpenedDecisionAction = MedRequestMutationFnProps & { action: 'reject' | 'hold' }

/** Records a rejection in the Clinical decision panel instead of sending it. */
export type PanelDecide = (input: {
  reason: string
  startConversation: boolean
  csNeedToContactPatient: boolean
}) => void

// MPS siblings get the opened request's decision; nothing is synced to the subscription for it.
const decisionPlanOf = (data: MedRequest, openedAction: OpenedDecisionAction): ActionPlan => {
  if (isMultiProductOrderMedRequest(data)) return multiProductOrderDecisionPlan(data, openedAction)

  return {
    actions: [
      openedAction,
      ...mpsFamilyDecisionActions(data, { action: openedAction.action, reason: openedAction.reason ?? '' })
    ],
    isChain: isMpsMedRequest(data)
  }
}

/** Reject / Follow Up from the sidebar views, or a rejection recorded in the Clinical decision panel. */
export default function useRejectOrFollowUp(onDecide?: PanelDecide) {
  const goNextRequest = useGoNextMedRequest(true)
  const { trackMedRequestAction } = usePosthogCustom()
  // The chain is awaited; per-mutation store resets would wipe the message mid-flight.
  const action = useMedRequestAction({
    enabled: true,
    doNotResetStore: true
  })
  const sendFanConsultation = useSendFanConsultation()
  const isPending = action.isPending || sendFanConsultation.isPending

  const send = async (data: MedRequest, openedAction: OpenedDecisionAction, fanQuestionnaireHash?: string) => {
    const plan = decisionPlanOf(data, openedAction)
    if (!plan.actions.length) return

    const isRejection = openedAction.action === 'reject'
    const trackDecision = () => trackMedRequestAction(isRejection ? 'reject' : 'contact', data.id)

    if (fanQuestionnaireHash) {
      await sendFanConsultation.mutateAsync({ medRequest: data, questionnaireHash: fanQuestionnaireHash })
    }
    // The FAN request holds the opened request itself, so only the rest of its family is actioned.
    const actions = fanQuestionnaireHash ? plan.actions.filter(item => item !== openedAction) : plan.actions

    if (!plan.isChain) {
      if (!fanQuestionnaireHash) action.mutate(actions[0])
      trackDecision()
      resetMedicationRequestStore()
      goNextRequest()
      return
    }

    const { setRejectOrFollowUpSubmitting } = useMedicationRequestStore.getState()
    setRejectOrFollowUpSubmitting(true)
    let chain: Awaited<ReturnType<typeof runMedRequestActionChain>>
    try {
      chain = await runMedRequestActionChain(action.mutateAsync, actions)
    } finally {
      setRejectOrFollowUpSubmitting(false)
    }

    if (fanQuestionnaireHash || chain.completed > 0) trackDecision()

    if (!chain.ok) {
      // Keeps the page and the typed message for a retry; the queue row was optimistically removed.
      invalidateRelevantActionQueries(data.patient.id ?? '', data.id)
      if (chain.total > 1) {
        toast({
          title: isRejection ? 'Rejection incomplete' : 'Follow-up incomplete',
          description: `${chain.completed} of ${chain.total} treatments were actioned; the rest are still pending. Please try again.`
        })
      }
      return
    }

    resetMedicationRequestStore()
    goNextRequest()
  }

  // `data` is the request the view decides: the opened one, or the panel's treatment.
  const submit = async (data: MedRequest, openedAction: OpenedDecisionAction, fanQuestionnaireHash?: string) => {
    if (!canSendMessages() || isPending || selectIsDecisionSubmitting(useMedicationRequestStore.getState())) {
      return
    }
    // The panel's treatment may be another item of the order, whose directions are on its own card.
    const { medicationDirection, multiProductOrderTreatments } = useMedicationRequestStore.getState()
    const card = multiProductOrderTreatments.find(treatment => treatment.id === data.id)
    if (!medReqFieldValidationNotifications(openedAction.action, card ? card.direction : medicationDirection)) return

    // The panel clears the message when it closes.
    if (onDecide) {
      return onDecide({
        reason: openedAction.reason ?? '',
        startConversation: !!openedAction.startConversation,
        csNeedToContactPatient: !!openedAction.csNeedToContactPatient
      })
    }
    return send(data, openedAction, fanQuestionnaireHash)
  }

  return { submit, isPending }
}
